/**
 * Pair of bar charts: Total Emissions (log scale) + Emission Intensity.
 * Used by both CompanyGHGPage and GHGOverviewPage.
 */

import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatNumber } from '../../utils/numberFormat';
import type { TransportModeChartEntry } from './ghgStatsUtils';

interface Props {
  /** All transport-mode entries (used for the EI chart). */
  allData: TransportModeChartEntry[];
  /** Entries filtered to those with TONS > 0 (used for the log-scale TONS chart). */
  tonsData: TransportModeChartEntry[];
}

const TransportModeChartPair: React.FC<Props> = ({ allData, tonsData }) => (
  <>
    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
      Emissions by Transport Mode
    </Typography>
    <Grid container spacing={3} mb={4}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            Total Emissions (Tonnes)
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tonsData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis
                scale="log"
                domain={[1, 'auto']}
                tick={{ fontSize: 11 }}
                width={80}
                tickFormatter={(v) => formatNumber(Number(v), 0)}
              />
              <RechartsTooltip
                formatter={(value: any) => [`${formatNumber(Number(value), 2)} T`, 'Emissions']}
                contentStyle={{ borderRadius: 8, fontSize: 13 }}
              />
              {/* Each datum carries its own `fill`, so recharts colours bars per-entry. */}
              <Bar dataKey="TONS" name="Emissions (T)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            Emission Intensity (gr/tkm)
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={allData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                width={80}
                tickFormatter={(v) => formatNumber(Number(v), 2)}
              />
              <RechartsTooltip
                formatter={(value: any) => [`${formatNumber(Number(value), 2)} gr/tkm`, 'EI']}
                contentStyle={{ borderRadius: 8, fontSize: 13 }}
              />
              {/* Each datum carries its own `fill`, so recharts colours bars per-entry. */}
              <Bar dataKey="EI" name="EI (gr/tkm)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>
    </Grid>
  </>
);

export default TransportModeChartPair;
